import {z} from 'zod'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {FormSection} from '@/components/commons/form-section/FormSection'
import {useTranslation} from '@/translations/i18n'
import {MedicalReportFieldsSchema, REPORT_TEXTAREA_LIMIT} from '../../formSchemas'
import {MedicalReportTextArea} from '../medical-report-text-area/MedicalReportTextArea'
import {PredefinedAdditionalTextsFields} from '../predefined-additional-text-fields/PredefinedAdditionalTextFields'
import {AttachedClinicalReportFields} from '../attached-clinical-report-fields/AttachedClinicalReportFields'
import {StyledMedicalReportTextArea} from './style'
import {MedicalReportProjectDiagnosisCard} from '@/features/medical-reports/components/medical-report-project-details-card/MedicalReportProjectDetailsCard'
import {MedicalReportTreatmentsFields} from '@/features/medical-reports/components/medical-report-treatments-fields/MedicalReportTreatmentsFields'
import {ErrorBox} from '@/components/commons/error-box/ErrorBox'
import {AlertCircleIcon} from '@/components/ui/icon/Icon'
import {MedicalReportSingleAccessesFields} from '@/features/medical-reports/components/medical-report-single-accesses-fields/MedicalReportSingleAccessesFields'
import {MedicalReportAbsenceAccessesFields} from '@/features/medical-reports/components/medical-report-absence-accesses-fields/MedicalReportAbsenceAccessesFields'
import {ExtensionRequestTotalDetails} from '@/features/medical-reports/components/extension-request-total-details/ExtensionRequestTotalDetails'

const ExtensionRequestSchema = MedicalReportFieldsSchema.pick({
    project: true,
    diagnosisNotes: true,
    additionalNotes: true
})
type ExtensionRequestSchema = z.infer<typeof ExtensionRequestSchema>

export const ExtensionRequestSections = () => {
    const {t} = useTranslation()

    const form = useFormContext<ExtensionRequestSchema>()
    const project = useWatch({control: form.control, name: 'project'})

    if (!project) {
        return (
            <div style={{padding: 12}}>
                <ErrorBox icon={<AlertCircleIcon />} title={t('medical_records:project_not_found')} />
            </div>
        )
    }

    return (
        <>
            <FormSection title={t('commons:diagnosis')} hasBottomDivider>
                <MedicalReportProjectDiagnosisCard project={project} />
            </FormSection>

            <FormSection title={t('medical_records:diagnosis_notes')} hasBottomDivider>
                <Controller
                    control={form.control}
                    name="diagnosisNotes"
                    render={({field, fieldState}) => (
                        <MedicalReportTextArea
                            rows={4}
                            maxLength={REPORT_TEXTAREA_LIMIT}
                            title={t('medical_records:diagnosis_notes')}
                            placeholder={t('commons:description_dot')}
                            errorMessage={fieldState.error?.message}
                            currentField="diagnosisNotes"
                            onChange={field.onChange}
                            value={field.value}
                        />
                    )}
                />
            </FormSection>

            <FormSection
                title={t('planned_accesses:treatments')}
                subTitle={t('planned_accesses:total_treatments')}
                hasBottomDivider
            >
                <MedicalReportTreatmentsFields project={project} />
            </FormSection>

            <FormSection
                title={t('commons:single_accesses')}
                subTitle={t('planned_accesses:total_treatments')}
                hasBottomDivider
            >
                <MedicalReportSingleAccessesFields project={project} />
            </FormSection>

            <FormSection title={t('projects:access_in_absence')} hasBottomDivider>
                <MedicalReportAbsenceAccessesFields project={project} />
            </FormSection>

            <FormSection title={t('commons:totals')} hasBottomDivider>
                <ExtensionRequestTotalDetails projectId={project.id} />
            </FormSection>

            <FormSection title={t('medical_records:predefined_additional_texts_title')} hasBottomDivider>
                <PredefinedAdditionalTextsFields />
            </FormSection>

            <FormSection title={t('medical_records:attached_clinical_report')} hasBottomDivider>
                <AttachedClinicalReportFields />
            </FormSection>

            <FormSection title={t('medical_records:additional_notes')}>
                <Controller
                    control={form.control}
                    name="additionalNotes"
                    render={({field, fieldState}) => (
                        <StyledMedicalReportTextArea
                            rows={2}
                            maxLength={REPORT_TEXTAREA_LIMIT}
                            title={t('medical_records:additional_notes')}
                            placeholder={t('commons:description_dot')}
                            errorMessage={fieldState.error?.message}
                            currentField="additionalNotes"
                            onChange={field.onChange}
                            value={field.value}
                        />
                    )}
                />
            </FormSection>
        </>
    )
}
